import { MaterialCommunityIcons } from '@expo/vector-icons';
import { FeatureName, useIsFeatureEnabled } from '@gripp/shared-logic';
import React, { useEffect, useState } from 'react';
import { ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';

import { Chip, Text, TextInput } from 'react-native-paper';
import { Colors } from '../../themes';
import { LabelSearchInput } from '../filter/labelSearchInput';
import { MultiItemSelector as MultiItemSelectorOld } from './multiItemSelector-old';
import { multiItemSelectorStore } from './multiItemSelectorStore';

export type Item = {
  id: string;
  name: string;
};

export type MultiItemSelectorProps = {
  selectedItems: Item[];
  availableItems: Item[];
  onItemsChange: (items: Item[]) => void;
  onCreateItem?: (name: string) => Promise<Item>;
  disabled?: boolean;
  title?: string;
  subtitle?: string;
  placeholder: string;
  searchPlaceholder: string;
  createButtonText: string;
  containerBackgroundColor?: string;
  itemType?: 'group' | 'category' | 'label';
  emptyText?: string;
  useCropCircle?: boolean;
  onSelectorEdit?: () => void;
};

export const MultiItemSelector: React.FC<MultiItemSelectorProps> = (props) => {
  const isCropCircle = useIsFeatureEnabled(FeatureName.CropCircleRedesign);

  if (isCropCircle) {
    return <MultiItemSelectorBase {...props} />;
  }

  return <MultiItemSelectorOld {...props} />;
};
  
export const MultiItemSelectorBase: React.FC<MultiItemSelectorProps> = ({
  selectedItems,
  availableItems,
  onItemsChange,
  onCreateItem,
  disabled = false,
  title,
  subtitle,
  placeholder,
  searchPlaceholder,
  createButtonText,
  containerBackgroundColor = 'transparent',
  useCropCircle,
  onSelectorEdit,
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const [search, setSearch] = useState('');
  const [filteredItems, setFilteredItems] = useState<Item[]>([]);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    const filtered = availableItems.filter(
      (item) =>
        item &&
        item.name &&
        item.name.toLowerCase().includes(search.toLowerCase()) &&
        !selectedItems.some((selected) => selected.id === item.id)
    );
    setFilteredItems(filtered);
  }, [availableItems, selectedItems, search]);

  const handleItemToggle = (item: Item) => {
    const isSelected = selectedItems.some((i) => i.id === item.id);
    if (isSelected) {
      onItemsChange(selectedItems.filter((i) => i.id !== item.id));
    } else {
      onItemsChange([...selectedItems, item]);
    }
  };

  const handleCreateItem = async () => {
    if (!search.trim() || !onCreateItem) return;

    setIsCreating(true);
    try {
      const newItem = await onCreateItem(search.trim());
      onItemsChange([...selectedItems, newItem]);
      setSearch('');
      setShowOptions(false);
    } catch (error) {
      console.error('Failed to create item:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleRemoveItem = (itemId: string) => {
    onItemsChange(selectedItems.filter((i) => i.id !== itemId));
  };

  const canCreateItem =
    !!search.trim() &&
    !availableItems.some(
      (item) => item.name.toLowerCase() === search.trim().toLowerCase()
    );

  return (
    <View
      style={{ backgroundColor: containerBackgroundColor }}
    >
      {title && (
        <Text style={useCropCircle ? styles.titleCropCircle : styles.title}>
          {title}
        </Text>
      )}
      {subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}

      {/* Dropdown Trigger with Selected Items */}
      {useCropCircle ? (
        <View style={[styles.pillsRow, disabled && styles.disabled]}>
          {selectedItems.map((item) => (
            <View key={item.id} style={styles.darkPill}>
              <Text style={styles.darkPillText} numberOfLines={1}>
                {item.name}
              </Text>
              {!disabled && (
                <TouchableOpacity
                  onPress={() => handleRemoveItem(item.id)}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <MaterialCommunityIcons
                    name="close"
                    size={12}
                    color="#FCFCFC"
                  />
                </TouchableOpacity>
              )}
            </View>
          ))}
          {!disabled && (
            <TouchableOpacity
              style={styles.addButton}
              onPress={() => {
                if (onSelectorEdit) {
                  multiItemSelectorStore.request(onItemsChange, selectedItems);
                  onSelectorEdit();
                } else {
                  setShowOptions(!showOptions);
                }
              }}
            >
              <MaterialCommunityIcons
                name={showOptions ? 'chevron-up' : 'plus'}
                size={16}
                color="#1A1A1A"
              />
            </TouchableOpacity>
          )}
        </View>
      ) : (
        <TouchableOpacity
          style={[
            styles.selectionsContainer,
            disabled && styles.disabled,
          ]}
          onPress={() => !disabled && setShowOptions(!showOptions)}
          disabled={disabled}
        >
          <View style={styles.chipsContainer}>
            {selectedItems.length > 0 ? (
              selectedItems.map((item) => (
                <Chip
                  key={item.id}
                  style={styles.chip}
                  textStyle={styles.chipText}
                  compact
                  onClose={disabled ? undefined : () => handleRemoveItem(item.id)}
                  closeIcon={disabled ? undefined : 'close'}
                  ellipsizeMode="tail"
                >
                  {item.name}
                </Chip>
              ))
            ) : (
              <Text style={styles.placeholderText}>{placeholder}</Text>
            )}
          </View>
          <View style={styles.chevronContainer}>
            <TextInput.Icon
              size={20}
              icon={showOptions ? 'chevron-up' : 'chevron-down'}
              color={Colors.grayText}
              onPress={() => !disabled && setShowOptions(!showOptions)}
            />
          </View>
        </TouchableOpacity>
      )}

      {/* Dropdown Options */}
      {showOptions && (
        <View style={styles.optionsListMainContainer}>
          <LabelSearchInput
            value={search}
            onChangeText={setSearch}
            placeholder={searchPlaceholder}
          />

          <View style={styles.optionsListContainer}>
            <ScrollView
              style={{ maxHeight: 400 }}
              contentContainerStyle={styles.itemsGrid}
              nestedScrollEnabled
            >
              {filteredItems.map((item) => (
                <TouchableOpacity
                  key={item.id}
                  style={styles.itemPill}
                  onPress={() => handleItemToggle(item)}
                >
                  <Text style={styles.itemPillText}>{item.name}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>

            {canCreateItem && onCreateItem && (
              <TouchableOpacity
                style={styles.createItemButton}
                onPress={handleCreateItem}
                disabled={isCreating}
              >
                <MaterialCommunityIcons
                  name="plus-circle-outline"
                  size={16}
                  color={Colors.primaryDark}
                  style={styles.createItemIcon}
                />
                <Text style={styles.createItemButtonText}>
                  {createButtonText} "{search.trim()}"
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  title: {
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 19,
    marginBottom: 8,
    color: Colors.black,
  },
  subtitle: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 16,
    marginBottom: 8,
    color: Colors.grayText,
  },
  titleCropCircle: {
    fontSize: 14,
    fontWeight: '600',
    lineHeight: 20,
    marginBottom: 4,
    color: '#1A1D24',
  },
  pillsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
  },
  darkPill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#020202',
    borderRadius: 20,
    height: 40,
    paddingHorizontal: 16,
    gap: 8,
  },
  darkPillText: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 18,
    color: '#FCFCFC',
    maxWidth: 120,
  },
  addButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#F3F3F3',
    alignItems: 'center',
    justifyContent: 'center',
  },
  selectionsContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: 8,
    borderWidth: 1,
    backgroundColor: Colors.white,
    borderColor: Colors.tabInactive,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  disabled: {
    opacity: 0.5,
  },
  chipsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginRight: 8,
    rowGap: 4,
  },
  chip: {
    backgroundColor: Colors.primaryGrayBackground,
    alignSelf: 'flex-start',
    maxWidth: 140,
    marginHorizontal: 5,
    marginVertical: 4,
    borderRadius: 16,
    minHeight: 28,
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chipText: {
    fontSize: 14,
    fontWeight: '400',
    color: Colors.black,
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
    marginBottom: 0,
    includeFontPadding: false,
  },
  placeholderText: {
    color: Colors.grayDisabled,
    fontSize: 16,
    marginLeft: 10,
    paddingVertical: 7,
  },
  chevronContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingRight: 8,
  },
  optionsListMainContainer: {
    marginTop: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.tabInactive,
    backgroundColor: Colors.white,
  },
  optionsListContainer: {
    marginBottom: 10,
    maxHeight: 400,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  itemsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    rowGap: 4,
  },
  itemPill: {
    backgroundColor: Colors.primaryGrayBackground,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 15,
    alignSelf: 'flex-start',
  },
  itemPillText: {
    fontSize: 14,
    lineHeight: 16,
    color: Colors.black,
    fontWeight: '400',
  },
  createItemButton: {
    backgroundColor: Colors.primaryGrayBackground,
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
    width: '100%',
  },
  createItemIcon: {
    marginRight: 8,
  },
  createItemButtonText: {
    fontSize: 14,
    lineHeight: 16,
    color: Colors.primaryDark,
    fontWeight: '500',
  },
});
